import { useId } from 'react';
import { Icon } from '../../icons/Icon';
import styles from './ExternalLink.module.css';

/**
 * The visually hidden end of an external link's name, so a screen-reader user hears that it leaves the
 * page (WCAG 3.2.5) — the same words `AccountProfileGrid`'s website link uses.
 */
export const OPENS_IN_A_NEW_TAB = '(opens in a new tab)';

export interface ExternalLinkProps {
    /**
     * Where the link goes. **Untrusted** — a search hit's URL. Only an absolute `http`/`https` URL becomes a
     * link; anything else renders as plain text.
     */
    readonly href: string;
    /**
     * What the linked page is — a search hit's title — read as the link's accessible description. Untrusted
     * text, rendered as a text child. Omitted or `null`: no description.
     */
    readonly description?: string | null | undefined;
    readonly 'data-testid'?: string | undefined;
}

/** The parsed URL when `href` is an absolute `http`/`https` URL, else `null`. */
function webUrl(href: string): URL | null {
    try {
        const url = new URL(href);
        return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
    } catch {
        return null;
    }
}

/**
 * A link to a page on another site, shown as that site's host (F11-c spec FR-V2, AC-25; WI-1.4 plan
 * Decision 9): `target="_blank"` with `rel="noopener noreferrer"`, the host as the visible text, a visually
 * hidden *(opens in a new tab)* and the registry's `externalLink` glyph after the text, `aria-hidden` like every
 * `Icon`. The host is `URL.hostname` exactly as the URL parser gives it — lower case, an international domain in
 * its `xn--` form — so a look-alike host cannot pass for another.
 *
 * **Only an absolute `http`/`https` URL becomes a link.** A `javascript:` value, a relative path, a
 * scheme-less `www.…` or an unparsable string renders as a `span` holding the value as text, with no icon and
 * no link: the value is still shown, and nothing the component cannot vouch for reaches an `href`.
 *
 * The description, when present, is the link's accessible description through `aria-describedby` on a
 * visually hidden span — never the `title` attribute, which only a pointer's hover shows. Two links to the same
 * host carry the same name; their context — the statement beside each and its description — tells them apart
 * (WCAG 2.4.4, design-system §5 *External link*).
 */
export function ExternalLink({ href, description, 'data-testid': testId }: ExternalLinkProps) {
    const descriptionId = useId();
    const url = webUrl(href);
    if (url === null) {
        return (
            <span className={styles.plain} data-testid={testId}>
                {href}
            </span>
        );
    }
    const described = description !== undefined && description !== null && description !== '';
    return (
        <>
            <a
                aria-describedby={described ? descriptionId : undefined}
                className={styles.link}
                data-testid={testId}
                href={url.href}
                rel="noopener noreferrer"
                target="_blank"
            >
                <span className={styles.host}>{url.hostname}</span>{' '}
                <span className={styles.visuallyHidden}>{OPENS_IN_A_NEW_TAB}</span>
                <Icon name="externalLink" size="sm" />
            </a>
            {described ? (
                <span className={styles.visuallyHidden} id={descriptionId}>
                    {description}
                </span>
            ) : null}
        </>
    );
}
